/**
 * ArticlesSchemeFilter plugin — headless preset filter (articles context).
 *
 * Mirrors the calls SchemeFilter but recognizes a THIRD value `pozostale`
 * (residual bucket). Distinct from the calls plugin because the calls
 * `SchemeValue` (kfs|bur) must NOT silently degrade `pozostale` and because the
 * articles scheme has its own per-type predicate semantics.
 *
 * Does NOT render a UI component (placement: "hidden"). UI is exposed via preset
 * pills in SavedViewsBar (KFS · BUR · Pozostałe · Wszystkie).
 *
 * Semantics:
 * - kfs       → resolved program type = NATIONAL_FUND
 * - bur       → resolved program type IN (EU_NATIONAL, EU_REGIONAL)
 * - pozostale → NO resolved program (source without project and no call mapping)
 *
 * @see docs/specifications/2026-06-08-articles-filters-unification/spec.md
 */

import { FilterPlugin } from "../../calls/filters/types";
import {
  SavedViewFilters,
  ArticlesSchemeValue,
} from "../../../../services/grantsApi";

const VALID_SCHEMES: ReadonlySet<string> = new Set(["kfs", "bur", "pozostale"]);

export const articlesSchemeFilter: FilterPlugin<
  ArticlesSchemeValue | undefined
> = {
  key: "scheme",
  label: "Schemat",
  placement: "hidden",
  order: 0,
  defaultValue: undefined,
  Component: null,

  isActive(value: ArticlesSchemeValue | undefined): boolean {
    return value !== undefined;
  },

  toUrlParams(
    value: ArticlesSchemeValue | undefined,
    params: URLSearchParams
  ): void {
    if (value !== undefined) {
      params.set("scheme", value);
    }
  },

  fromUrlParams(params: URLSearchParams): ArticlesSchemeValue | undefined {
    const raw = params.get("scheme");
    if (raw !== null && VALID_SCHEMES.has(raw)) {
      return raw as ArticlesSchemeValue;
    }
    return undefined;
  },

  toSavedView(
    value: ArticlesSchemeValue | undefined,
    filters: Partial<SavedViewFilters>
  ): void {
    if (value !== undefined) {
      // scheme is serialized into the shared filters bag; the articles
      // saved-view schema accepts the 'pozostale' value.
      // TECH-DEBT: FilterPlugin.toSavedView is typed against the calls-only
      // SavedViewFilters bag (scheme: SchemeValue = kfs|bur), so the articles
      // 'pozostale' value requires this loose cast. Fixing it would mean
      // generifying FilterPlugin over the saved-view shape (shared API owned by
      // another module) — out of scope for these findings.
      (filters as Record<string, unknown>).scheme = value;
    }
  },

  fromSavedView(filters: SavedViewFilters): ArticlesSchemeValue | undefined {
    // TECH-DEBT: see toSavedView — articles 'pozostale' scheme read via loose
    // cast because FilterPlugin is typed against the calls-only SavedViewFilters.
    const raw = (filters as Record<string, unknown>).scheme;
    if (typeof raw === "string" && VALID_SCHEMES.has(raw)) {
      return raw as ArticlesSchemeValue;
    }
    return undefined;
  },

  toApiParams(
    value: ArticlesSchemeValue | undefined,
    params: URLSearchParams
  ): void {
    if (value !== undefined) {
      params.set("scheme", value);
    }
  },
};
